import React, { useState, useEffect } from 'react';
import { getIntegrations } from '../../_services/ApiService';
import { Table, TableBody, TableCell, TableHead, TableRow, styled } from '@mui/material';
import { FullScreenLoader } from 'components/Loader';
import { getJobs } from '_services/ApiService';
import { DownloadCSVButton } from 'components/DownloadCSVButton';

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(3)
}));

const Content = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2)
}));

const RenderRow = ({row, keys}) => {
  return <TableRow>
    {keys.map(key => <TableCell>{row[key]}</TableCell>)}
  </TableRow>
}

const RenderReport = ({content}) => {
  const keys = [...new Set(content.flatMap(r => Object.keys(r)))]
  return <Table border>
    <TableHead>
      <TableRow>
        {keys.map(k => <TableCell>{k}</TableCell>)}
      </TableRow>
    </TableHead>
    <TableBody>
      {content.map(row => <RenderRow row={row} keys={keys} />)}
    </TableBody>

  </Table>
}

export const ClientIntegrations = () => {
  const [loading, setLoading] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [integrations, setIntegrations] = useState([]);

  const refreshJobs = async () => {
    setLoading(x => x + 1);
    setJobs([]);
    try {
      const result = await getJobs();
      setJobs(result);
    } finally {
      setLoading(x => x - 1);
    }
  };

  const refreshIntegrations = async () => {
    setLoading(x => x + 1);
    setIntegrations([]);
    try {
      const result = await getIntegrations({pageSize: 1000});
      setIntegrations(result);
    } finally {
      setLoading(x => x - 1);
    }
  };

  useEffect(() => {
    refreshJobs();
  }, []);
  useEffect(() => {
    refreshIntegrations();
  }, []);

  const goodJobs = (() => {
    return jobs
      .filter(job => !job.jobName.toLowerCase().includes("admin"))
      .filter(job => job.status !== 'retired')
      .filter(job => job.status !== 'disabled')
      .filter(job => job.config.companyName !== undefined)
      .filter(job => job.config.companyName !== 'Wurkforce')
      .filter(job => job.config.companyName !== 'EnjoyWurk')
  })()

  const byClient = (() => {
    const result = {}
    for(let job of goodJobs) {
      const company = job.config.companyName
      if(!result[company]) {
        result[company] = []
      }
      result[company].push(job)
    }
    return result
  })()

  const cleanClientJobs = (jobs) => {
    const result = []
    const pcs = jobs.filter(job => job.jobType.includes("mep"))
    const nonPcs = jobs.filter(job => !job.jobType.includes("mep"))
    if(pcs.length > 0) {
      result.push({integration: "PCS/MEP", type: "PCS/MEP"})
    }
    for(let j of nonPcs) {
      result.push({integration: j.jobName, type: `job: ${j.jobType}`})
    }
    return result
  }

  const cleanIntegrations = (() => {
    return integrations.data
      ?.filter(i => i.integration !== 'document-migration')
      .filter(i => i.company?.toLowerCase() !== 'demoanddocumentation')
      .map(i => {
        const config = i.config
        if(i.config?.authentication) {
          return {company: i.companyName, integration: i.eventType, active: new Date() - new Date(i.config.authentication.refreshedDate) < 30 * 24 * 3600 * 1000}
        } else if(i.config?.kronos) {
          return {company: i.companyName, integration: i.eventType, active: new Date() - new Date(i.config.kronos.refreshedDate) < 30 * 24 * 3600 * 1000}
        }
      })
      .filter(i => i !== undefined)
      .filter(i => i.active)
      .map(i => {
        const type = (i.integration.includes("lever")
          ? "Lever"
          : i.integration.includes("greenhouse-application")
          ? "Greenhouse Application"
          : i.integration.includes("greenhouse-onboarding")
          ? "Greenhouse Onboarding"
          : ""
        )
        return {...i, type}
      })
      .map(({active, ...rest}) => rest)
  })() || []

  const report = (() => {
    const result = [...cleanIntegrations]
    for(let [company, jobs] of Object.entries(byClient)) {
      for(let job of cleanClientJobs(jobs)) {
        result.push({company, ...job})
      }
    }
    return result
  })()

  const sortedReport = report.sort((a, b) => {
    if(a.company.toUpperCase() > b.company.toUpperCase()) {
      return 1
    } else if(a.company.toUpperCase() < b.company.toUpperCase()) {
      return -1
    } else {
      return 0
    }
  })

  return (
    <Root>
      { loading ? <FullScreenLoader /> : "" }
      <Content>
        <h2>
          Client integrations
          &nbsp;
          <DownloadCSVButton
            headers={['company', 'integration', 'type']}
            content={sortedReport}
            fileName={`integrations-report-${new Date().toISOString()}.csv`}
            >Download CSV
          </DownloadCSVButton>
        </h2>
        <RenderReport content={sortedReport} />
      </Content>
    </Root>
  );
};


// greenhouse
// lever
